import React from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { RunDetailSearchParams } from '../../router';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Page,
  PageHeader,
  Card,
  CardContent,
  DataTable,
  Text,
  Flex,
  Button,
  LoadingOrbit,
  GaugeChart,
  LineChart,
  useToast,
  DangerSection,
  DangerAction,
  DialogTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Play, X, Eye, ArrowClockwise, Trash } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import {
  useQaRun,
  useQaRunReports,
  useQaRunReportTimeline,
  useCancelQaRun,
  useRerunPageTest,
  useDeleteQaRun,
} from '../../features/qa-runs/useQaRuns';
import {
  getQaRunStatusMeta,
  type QaRunStatus,
  type QaReportListItem,
  type QaReportStatus,
} from '../../features/qa-runs/qaRunTypes';

const formatDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string => {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
};

const formatRelativeTimeAgo = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) return 'Unknown';

  const deltaMs = Date.now() - timestamp;
  const deltaSeconds = Math.floor(Math.abs(deltaMs) / 1000);
  const future = deltaMs < 0;

  if (deltaSeconds < 10) return future ? 'in a few seconds' : 'just now';
  if (deltaSeconds < 60) return future ? `in ${deltaSeconds}s` : `${deltaSeconds}s ago`;

  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) return future ? `in ${deltaMinutes}m` : `${deltaMinutes}m ago`;

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) return future ? `in ${deltaHours}h` : `${deltaHours}h ago`;

  const deltaDays = Math.floor(deltaHours / 24);
  return future ? `in ${deltaDays}d` : `${deltaDays}d ago`;
};

const StatusBadge: React.FC<{ status: QaRunStatus }> = ({ status }) => {
  const meta = getQaRunStatusMeta(status);
  const colorMap: Record<string, 'success' | 'secondary' | 'warning' | 'destructive' | 'outline'> =
    {
      green: 'success',
      blue: 'secondary',
      yellow: 'warning',
      red: 'destructive',
      gray: 'outline',
    };
  return (
    <TagBadge
      name={meta.label}
      color={badgeVariantToTagColor(colorMap[meta.color] ?? 'outline')}
      hideDot
    />
  );
};

const ReportStatusBadge: React.FC<{ status: QaReportStatus }> = ({ status }) => {
  const variantMap: Record<
    QaReportStatus,
    'success' | 'secondary' | 'destructive' | 'warning' | 'outline'
  > = {
    no_issues: 'success',
    running: 'secondary',
    issues_found: 'warning',
    failed: 'destructive',
    pending: 'outline',
  };
  const labelMap: Record<QaReportStatus, string> = {
    no_issues: 'No Issues',
    running: 'Running',
    issues_found: 'Issues Found',
    failed: 'Failed',
    pending: 'Pending',
  };
  return (
    <TagBadge
      name={labelMap[status]}
      color={badgeVariantToTagColor(variantMap[status])}
      hideDot
      size="sm"
    />
  );
};

export const RunDetail: React.FC = () => {
  const navigate = useNavigate();
  const { runId, propertyId } = useParams({ from: '/runs/$propertyId/$runId' });
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  // Read pagination from URL search params
  const searchParams = useSearch({ strict: false }) as RunDetailSearchParams;
  const page = searchParams.page ?? 1;
  const pageSize = searchParams.pageSize ?? 20;

  // Helper to update URL search params
  const updateSearchParams = React.useCallback(
    (updates: Partial<RunDetailSearchParams>) => {
      const next = { ...searchParams, ...updates };
      const cleaned: RunDetailSearchParams = {};
      if (next.page && next.page !== 1) cleaned.page = next.page;
      if (next.pageSize && next.pageSize !== 20) cleaned.pageSize = next.pageSize;
      navigate({
        to: '/runs/$propertyId/$runId',
        params: { propertyId, runId },
        search: cleaned,
        replace: true,
      });
    },
    [navigate, propertyId, runId, searchParams]
  );

  const runQuery = useQaRun(runId);
  const reportsQuery = useQaRunReports(runId, { page, pageSize });
  const cancelMutation = useCancelQaRun();
  const rerunMutation = useRerunPageTest(runId);
  const deleteMutation = useDeleteQaRun();

  const run = runQuery.data?.run;
  const isRunning = run?.status === 'running' || run?.status === 'pending';

  const timelineQuery = useQaRunReportTimeline(runId, {
    bucketMinutes: 5,
    refetchInterval: isRunning ? 3000 : false,
  });

  // Compute totals from timeline data (already fetched for the chart)
  const timelineTotals = React.useMemo(() => {
    if (!timelineQuery.data?.buckets) return { passed: 0, failed: 0 };
    return timelineQuery.data.buckets.reduce(
      (acc, bucket) => ({
        passed: acc.passed + bucket.passed,
        failed: acc.failed + bucket.failed,
      }),
      { passed: 0, failed: 0 }
    );
  }, [timelineQuery.data?.buckets]);

  const passRateTotal = timelineTotals.passed + timelineTotals.failed;
  const passRate =
    passRateTotal > 0 ? Math.round((timelineTotals.passed / passRateTotal) * 100) : 0;

  useBreadcrumbs(
    run
      ? [
          { label: 'Runs', onPress: () => navigate({ to: '/runs' }) },
          { label: 'QA Runs', onPress: () => navigate({ to: '/runs/qa' }) },
          { label: `Run ${run.id.slice(0, 8)}` },
        ]
      : [{ label: 'Runs', onPress: () => navigate({ to: '/runs' }) }]
  );

  const handleCancel = async () => {
    if (!runId) return;
    try {
      await cancelMutation.mutateAsync(runId);
      toast.success('QA run canceled');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel run');
    }
  };

  const handleRerun = async (reportId: string) => {
    try {
      await rerunMutation.mutateAsync(reportId);
      toast.success('Re-running page test...');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to re-run test');
    }
  };

  const handleDelete = async () => {
    if (!runId) return;
    try {
      await deleteMutation.mutateAsync(runId);
      toast.success('QA run deleted');
      setDeleteDialogOpen(false);
      navigate({ to: '/runs/$propertyId', params: { propertyId } });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete run');
    }
  };

  const reportColumns: ColumnDef<StockFeatures, QaReportListItem>[] = [
    {
      accessorKey: 'pageUrl',
      header: 'Page',
      cell: ({ row }) => (
        <div className="min-w-0">
          <Text size="sm" className="truncate block" title={row.original.pageUrl}>
            {row.original.pageTitle || row.original.pageUrl}
          </Text>
          {row.original.pageTitle && (
            <Text size="xs" className="text-muted-foreground truncate block">
              {row.original.pageUrl}
            </Text>
          )}
        </div>
      ),
      meta: { flex: 3 },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <ReportStatusBadge status={row.original.status} />,
      meta: { flex: 1 },
    },
    {
      accessorKey: 'healthyCount',
      header: 'Healthy',
      cell: ({ row }) => (
        <Text size="sm" className="text-success-600">
          {row.original.healthyCount}
        </Text>
      ),
      meta: { flex: 1 },
    },
    {
      accessorKey: 'unhealthyCount',
      header: 'Unhealthy',
      cell: ({ row }) => (
        <Text size="sm" className={row.original.unhealthyCount > 0 ? 'text-destructive' : ''}>
          {row.original.unhealthyCount}
        </Text>
      ),
      meta: { flex: 1 },
    },
    {
      id: 'actions',
      header: () => <div className="text-right w-full">Actions</div>,
      cell: ({ row }) => {
        const canRerun = row.original.status !== 'pending' && row.original.status !== 'running';
        return (
          <Flex gap="sm" justify="end" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => handleRerun(row.original.id)}
              isDisabled={!canRerun || rerunMutation.isPending}
              iconLeft={<ArrowClockwise size={14} />}
            >
              Re-run
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onPress={() =>
                navigate({
                  to: '/pages/$propertyId/canonical/$canonicalPageId',
                  params: {
                    propertyId: propertyId ?? '',
                    canonicalPageId: row.original.canonicalPageId,
                  },
                })
              }
              iconLeft={<Eye size={14} />}
            >
              View
            </Button>
          </Flex>
        );
      },
      meta: { flex: 2 },
    },
  ];

  if (runQuery.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  if (runQuery.isError || !run) {
    return (
      <Page>
        <PageHeader title="Run Not Found" description="The requested QA run could not be found" />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This run may have been deleted or you may not have access.
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => navigate({ to: '/runs' })}
            className="mt-4"
          >
            Back to Runs
          </Button>
        </div>
      </Page>
    );
  }

  const headerDescription = (
    <Flex align="center" justify="between" gap="sm" wrap>
      <Flex align="center" gap="xs" wrap>
        {(() => {
          const parts: React.ReactNode[] = [];

          if (typeof run.propertyName === 'string' && run.propertyName.trim().length > 0) {
            parts.push(
              <Text key="property" size="sm" className="text-muted-foreground">
                {run.propertyName}
              </Text>
            );
          }

          parts.push(
            <Text key="duration" size="sm" className="text-muted-foreground">
              Duration {formatDuration(run.startedAt, run.finishedAt)}
            </Text>
          );

          parts.push(
            <Text key="started" size="sm" className="text-muted-foreground">
              Started {formatRelativeTimeAgo(run.startedAt ?? run.createdAt)}
            </Text>
          );

          return parts.map((node, idx) => (
            <React.Fragment key={idx}>
              {node}
              {idx < parts.length - 1 && (
                <Text size="sm" className="text-muted-foreground">
                  /
                </Text>
              )}
            </React.Fragment>
          ));
        })()}
      </Flex>
    </Flex>
  );

  return (
    <Page>
      <PageHeader
        icon={<Play size={24} weight="fill" />}
        eyebrow={<StatusBadge status={run.status} />}
        title="QA Run"
        description={headerDescription}
        actions={
          isRunning ? (
            <Button
              variant="destructive"
              size="sm"
              onPress={handleCancel}
              isDisabled={cancelMutation.isPending}
              iconLeft={<X size={16} />}
            >
              {cancelMutation.isPending ? 'Canceling...' : 'Cancel Run'}
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-6">
        {/* Run activity timeline */}
        <Card padding="md">
          <CardContent>
            {timelineQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingOrbit size="md" />
              </div>
            ) : timelineQuery.isError ? (
              <Text size="sm" className="text-destructive">
                {timelineQuery.error instanceof Error
                  ? timelineQuery.error.message
                  : 'Failed to load activity timeline'}
              </Text>
            ) : timelineQuery.data && timelineQuery.data.buckets.length > 0 ? (
              (() => {
                const timelineData = timelineQuery.data.buckets.map((bucket) => {
                  const bucketStart = new Date(bucket.bucketStart);
                  const label = new Intl.DateTimeFormat(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                  }).format(bucketStart);

                  const completed = bucket.passed + bucket.failed + bucket.notApplicable;
                  const _passRate =
                    completed > 0 ? Math.round((bucket.passed / completed) * 100) : 0;

                  return {
                    bucketStart: bucket.bucketStart,
                    label,
                    passed: bucket.passed,
                    failed: bucket.failed,
                    notApplicable: bucket.notApplicable,
                    completed,
                    hasData: completed > 0,
                  };
                });

                return (
                  <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
                    <div className="lg:col-span-2 space-y-4">
                      <div className="space-y-2">
                        <LineChart
                          height={140}
                          data={timelineData}
                          xDataKey="label"
                          lines={[
                            {
                              dataKey: 'passed',
                              name: 'Passed',
                              color: 'hsl(var(--success))',
                            },
                            {
                              dataKey: 'failed',
                              name: 'Failed',
                              color: 'hsl(var(--destructive))',
                            },
                          ]}
                          showLegend={false}
                          showYAxis={false}
                          showEmptyAsDashed={true}
                          hasDataKey="hasData"
                          xAxisInterval="preserveStartEnd"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col items-center justify-center">
                      <GaugeChart
                        score={passRate}
                        label="Pass Rate"
                        size="sm"
                        showScore={true}
                        showLabel={true}
                      />
                      <Text size="xs" className="text-muted-foreground mt-2 text-center">
                        {timelineTotals.passed} passed • {timelineTotals.failed} failed
                      </Text>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
                <Text className="text-muted-foreground">
                  {isRunning
                    ? 'Activity will appear here as tests complete...'
                    : 'No completed tests yet'}
                </Text>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {reportsQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingOrbit size="md" />
          </div>
        ) : reportsQuery.data && reportsQuery.data.reports.length > 0 ? (
          <DataTable
            columns={reportColumns}
            data={reportsQuery.data.reports}
            aria-label="QA reports"
            serverPagination={{
              page,
              pageSize,
              pageCount: Math.max(reportsQuery.data.pagination.totalPages, 1),
              totalItems: reportsQuery.data.pagination.totalItems,
              onPageChange: (nextPage: number) => updateSearchParams({ page: nextPage }),
              onPageSizeChange: (nextPageSize: number) =>
                updateSearchParams({ pageSize: nextPageSize }),
              isLoading: reportsQuery.isFetching,
            }}
          />
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
            <Text className="text-muted-foreground">
              {isRunning
                ? 'Test results will appear here as pages are tested...'
                : 'No test results found'}
            </Text>
          </div>
        )}

        {/* Danger Zone */}
        {!isRunning && (
          <>
            <DangerSection>
              <DangerAction
                title="Delete Run"
                description="Delete this QA run. Related crawls and reports will be preserved but unlinked."
              >
                <Button
                  variant="destructive"
                  size="sm"
                  onPress={() => setDeleteDialogOpen(true)}
                  isDisabled={deleteMutation.isPending}
                  iconLeft={<Trash size={16} />}
                >
                  Delete Run
                </Button>
              </DangerAction>
            </DangerSection>

            <DialogTrigger isOpen={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} size="sm">
              <span />
              <Dialog
                title="Delete QA run"
                description="Are you sure you want to delete this QA run?"
              >
                <DialogContent>
                  <Text>
                    This will delete the QA run record. Related crawls and reports will be preserved
                    but unlinked. This action cannot be undone.
                  </Text>
                </DialogContent>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onPress={() => setDeleteDialogOpen(false)}
                    isDisabled={deleteMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onPress={handleDelete}
                    isDisabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete Run'}
                  </Button>
                </DialogFooter>
              </Dialog>
            </DialogTrigger>
          </>
        )}
      </div>
    </Page>
  );
};
